import React, { useLayoutEffect, useRef, useState } from "react";
import { ArrowLeft, CalendarDays, ExternalLink, MapPin, Radio } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import CaseStudyCta from "../components/layout/CaseStudyCta";
import { arendalFacts, arendalDaily, arendalHourly, arendalTuesdays } from "../data/arendalsuka";
import "../styles/arendalsuka.css";

// ---- formatting helpers ---------------------------------------------------
// Dates are ISO strings (YYYY-MM-DD); split them rather than parse, so the
// visitor's time zone can never move a day.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAYS_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const ymd = (iso) => iso.split("-").map(Number);
const dayIndex = (iso) => {
  const [y, m, d] = ymd(iso);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};
const shortDate = (iso) => `${ymd(iso)[2]} ${MONTHS[ymd(iso)[1] - 1]}`;
const longDate = (iso) => `${ymd(iso)[2]} ${MONTHS_LONG[ymd(iso)[1] - 1]}`;
const dayShortDate = (iso) => `${DAYS[dayIndex(iso)]} ${shortDate(iso)}`;
const dayLongDate = (iso) => `${DAYS_LONG[dayIndex(iso)]} ${longDate(iso)}`;
const fmt = (n) => n.toLocaleString("en-GB");
const times = (a, b) => (a / b).toFixed(1);
const hh = (h) => `${String(h).padStart(2, "0")}:00`;

// ---- figures derived from the data file (never retyped) --------------------
const F = arendalFacts;
const YEAR = ymd(F.fest_from)[0];
const festRange = `${ymd(F.fest_from)[2]}–${longDate(F.fest_to)} ${YEAR}`;
const festRangeShort = `${ymd(F.fest_from)[2]}–${shortDate(F.fest_to)}`;
const festRangeNoYear = `${ymd(F.fest_from)[2]}–${longDate(F.fest_to)}`;
const firstDay = arendalDaily[0];
const lastDay = arendalDaily[arendalDaily.length - 1];
const windowRange = `${longDate(firstDay.d)} to ${longDate(lastDay.d)} ${YEAR}`;
const isFest = (r) => r.d >= F.fest_from && r.d <= F.fest_to;
const partDays = arendalDaily.filter((r) => r.partial).map((r) => shortDate(r.d)).join(", ");
const festDays = arendalDaily.filter(isFest);
const preDays = arendalDaily.filter((r) => r.d < F.fest_from && !r.partial);
const preMin = Math.min(...preDays.map((r) => r.peak));
const preMax = Math.max(...preDays.map((r) => r.peak));
const openDay = festDays[0];
const peakDay = arendalDaily.reduce((a, b) => (b.peak > a.peak ? b : a));
const festWeekend = festDays.filter((r) => r.dow === "Sat" || r.dow === "Sun");
const peakTime = F.peak_when.split(", ")[1];
const lastReading = F.last_data.split(" ")[1];

const [tueBefore, tueFest, tueAfter] = arendalTuesdays;

const hourFestPeak = arendalHourly.reduce((a, b) => (b.fest > a.fest ? b : a));
const hourNormPeak = arendalHourly.reduce((a, b) => (b.norm > a.norm ? b : a));
const hourAt3 = arendalHourly.find((r) => r.h === 3);

const firstFestIdx = arendalDaily.findIndex(isFest);
const lastFestIdx = firstFestIdx + festDays.length - 1;
const peakIdx = arendalDaily.indexOf(peakDay);
// Tallest day before the festival week, part days included: in the wide hero
// the headline sits above these columns.
const quietMax = Math.max(...arendalDaily.slice(0, firstFestIdx).map((r) => r.peak));

// The lower chart zooms in on the two weeks before the festival week, the week
// itself and the two days after it; the hero shows the whole period.
const zoomDays = arendalDaily.slice(Math.max(0, firstFestIdx - 14));
const zoomRange = `${longDate(zoomDays[0].d)} to ${longDate(lastDay.d)} ${YEAR}`;
const zoomPre = zoomDays.filter((r) => r.d < F.fest_from && !r.partial);
const zoomPreMin = Math.min(...zoomPre.map((r) => r.peak));
const zoomPreMax = Math.max(...zoomPre.map((r) => r.peak));
const zoomPartDays = zoomDays.filter((r) => r.partial).map((r) => shortDate(r.d)).join(", ");

// ---- shared chart plumbing -------------------------------------------------
// Charts are drawn at the width they are shown at, so text stays at its real
// size on a phone instead of shrinking with a fixed viewBox. Measured before
// paint, so the hero never flashes its wide layout on a phone.
const useSize = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const box = el.getBoundingClientRect();
      const next = { w: Math.round(box.width), h: Math.round(box.height) };
      if (next.w > 0) setSize((s) => (s.w === next.w && s.h === next.h ? s : next));
    };
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
};

const useWidth = (fallback) => {
  const [ref, size] = useSize();
  return [ref, size.w || fallback];
};

const niceMax = (value, step) => Math.ceil((value * 1.08) / step) * step;
const tickValues = (max, step) => Array.from({ length: Math.floor(max / step) + 1 }, (_, i) => i * step);

// A column with a 4px rounded data end and a square foot on the baseline.
const columnPath = (cx, top, bottom, width) => {
  const x0 = cx - width / 2;
  const x1 = cx + width / 2;
  const r = Math.max(0, Math.min(4, width / 2, bottom - top));
  return `M${x0},${bottom}V${top + r}Q${x0},${top} ${x0 + r},${top}H${x1 - r}Q${x1},${top} ${x1},${top + r}V${bottom}Z`;
};

const Tooltip = ({ x, y, lines, bounds }) => {
  const width = Math.max(...lines.map((l) => l.length)) * 6.6 + 18;
  const height = lines.length * 16 + 10;
  const left = Math.min(Math.max(x - width / 2, bounds.left), bounds.right - width);
  const top = Math.max(y - height - 8, 2);
  return (
    <g className="ar-tip" pointerEvents="none">
      <rect x={left} y={top} width={width} height={height} rx="4" />
      {lines.map((line, i) => (
        <text key={`${i}-${line}`} x={left + 9} y={top + 18 + i * 16}>{line}</text>
      ))}
    </g>
  );
};

// ---- hero: every daily peak on one scale -----------------------------------
const dailyDesc =
  `Daily peak of estimated devices in Arendal sentrum, ${windowRange}. ` +
  `From ${longDate(preDays[0].d)} to ${longDate(preDays[preDays.length - 1].d)} the daily peak ranged from ${fmt(preMin)} to ${fmt(preMax)}. ` +
  `During the festival week it reached ${fmt(openDay.peak)} on ${dayLongDate(openDay.d)} and ${fmt(peakDay.peak)} at ${peakTime} on ${dayLongDate(peakDay.d)}, ` +
  `${F.lift} times the median of all July readings, ` +
  `then fell to ${festWeekend.map((r) => `${fmt(r.peak)} on ${dayLongDate(r.d)}`).join(" and ")}. ` +
  `The median of all July readings, ${fmt(F.baseline)}, is drawn as a dashed line. ` +
  `${dayLongDate(firstDay.d)} and ${dayLongDate(lastDay.d)} are part days.`;

// From this stage width the copy sits over the quiet weeks; below it the copy
// stacks above the chart.
const HERO_WIDE = 840;

const ArendalHero = () => {
  const [stageRef, stage] = useSize();
  const [copyRef, copy] = useSize();
  const w = stage.w || 1200;
  const wide = w >= HERO_WIDE;
  const n = arendalDaily.length;
  const m = wide ? { top: 30, right: 96, bottom: 62 } : { top: 30, right: 50, bottom: 60 };
  const pw = w - m.right;
  const step = pw / n;
  const barW = Math.max(3, Math.min(14, step * (wide ? 0.52 : 0.6)));
  const x = (i) => step * i + step / 2;
  const bandLeft = step * firstFestIdx;
  const bandRight = step * (lastFestIdx + 1);
  const annRight = bandLeft - (wide ? 22 : 10);
  const copyWidth = wide ? Math.min(760, Math.round(annRight - 190 - 40)) : undefined;
  // One scale, topped by the peak. When the copy sits over the quiet weeks the
  // plot is made tall enough that their tallest day stays 40px below it.
  const ph = wide
    ? Math.max(400, Math.ceil((copy.h + 40 - m.top) / (1 - quietMax / peakDay.peak)))
    : Math.round(Math.min(340, Math.max(230, w * 0.55)));
  const y = (v) => m.top + ph - (v / peakDay.peak) * ph;
  const base = m.top + ph;
  const medY = y(F.baseline);
  const preX0 = x(0) - barW / 2;
  const preX1 = x(firstFestIdx - 1) + barW / 2;
  const festX0 = x(firstFestIdx) - barW / 2;
  const festX1 = x(lastFestIdx) + barW / 2;
  // The one-line range under "Five weeks before" is about 210px wide; on
  // phones narrower than about 390px it would run into the festival-week
  // label, so there it breaks onto a second line.
  const wrapPre = !wide && festX0 - preX0 < 230;
  const rail = base + 12;
  const bracket = (x0, x1) => `M${x0},${rail - 5}V${rail}H${x1}V${rail - 5}`;
  const h = base + m.bottom + (wrapPre ? 16 : 0);
  const numSize = wide ? 42 : 30;
  const annSize = wide ? 13 : 11.5;
  const annLead = wide ? 18 : 15;
  const annTop = m.top + (wide ? 13 : 9);

  return (
    <header className="arendal-hero">
      <div className="arendal-hero-inner">
        <Link to="/projects" className="arendal-back"><ArrowLeft size={15} aria-hidden="true" /> Projects</Link>
        <div ref={stageRef} className={`arendal-hero-stage${wide ? " is-wide" : ""}`}>
          <div ref={copyRef} className="arendal-hero-copy" style={wide ? { width: copyWidth } : undefined}>
            <p className="arendal-kicker">Event mobility · Arendal, Norway</p>
            <h1>Arendal town centre during Arendalsuka {YEAR}</h1>
            <p className="arendal-deck">
              Before, during and after Arendalsuka, Norway's annual democracy festival, we estimated how many mobile
              devices were in Arendal town centre every ten minutes.
            </p>
          </div>

          <svg
            className="arendal-hero-chart"
            width="100%"
            viewBox={`0 0 ${w} ${h}`}
            role="img"
            aria-labelledby="hero-peaks-title hero-peaks-desc"
          >
            <title id="hero-peaks-title">Daily peak of estimated devices in Arendal sentrum, {windowRange}</title>
            <desc id="hero-peaks-desc">{dailyDesc}</desc>

            <defs>
              <linearGradient id="arh-band-fade" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" className="arh-band-stop" stopOpacity="0" />
                <stop offset="0.45" className="arh-band-stop" stopOpacity="0.06" />
                <stop offset="1" className="arh-band-stop" stopOpacity="0.1" />
              </linearGradient>
            </defs>
            <rect x={bandLeft} y="0" width={bandRight - bandLeft} height={base} fill="url(#arh-band-fade)" />
            <line x1="0" x2={pw + 6} y1={medY} y2={medY} className="arh-median" />

            {arendalDaily.map((r, i) => (
              <path
                key={r.d}
                d={columnPath(x(i), y(r.peak) + (r.partial ? 0.6 : 0), base, r.partial ? barW - 1.2 : barW)}
                className={`arh-col${r.partial ? " is-part" : isFest(r) ? " is-fest" : ""}`}
                style={{ animationDelay: `${i * 12}ms` }}
              />
            ))}
            <line x1="0" x2={pw} y1={base} y2={base} className="arh-base" />

            <text x={pw + (wide ? 14 : 8)} y={medY + (wide ? -3 : -10)} className="arh-note">
              <tspan x={pw + (wide ? 14 : 8)}>{wide ? "July median" : "July"}</tspan>
              {!wide && <tspan x={pw + 8} dy="13">median</tspan>}
              <tspan x={pw + (wide ? 14 : 8)} dy={wide ? 15 : 13} className="arh-strong">{fmt(F.baseline)}</tspan>
            </text>

            <line x1={annRight + 8} x2={x(peakIdx) - barW / 2 - 5} y1={m.top} y2={m.top} className="arh-leader" />
            <text x={annRight} y={annTop} textAnchor="end" fontSize={numSize} className="arh-num">{fmt(peakDay.peak)}</text>
            <text x={annRight} y={annTop + annLead + (wide ? 8 : 6)} textAnchor="end" fontSize={annSize} className="arh-note">
              <tspan x={annRight} className="arh-strong">Highest reading</tspan>
              <tspan x={annRight} dy={annLead}>{peakTime}, {wide ? dayLongDate(peakDay.d) : dayShortDate(peakDay.d)}</tspan>
              <tspan x={annRight} dy={annLead}>{F.lift} times the July median</tspan>
            </text>

            <path d={bracket(preX0, preX1)} className="arh-rail" />
            <text x={preX0} y={rail + 20} className="arh-note">
              <tspan x={preX0} className="arh-strong">Five weeks before</tspan>
              {wrapPre ? (
                <>
                  <tspan x={preX0} dy="16">Daily peak between</tspan>
                  <tspan x={preX0} dy="16">{fmt(preMin)} and {fmt(preMax)}</tspan>
                </>
              ) : (
                <tspan x={preX0} dy="16">Daily peak between {fmt(preMin)} and {fmt(preMax)}</tspan>
              )}
            </text>
            <path d={bracket(festX0, festX1)} className="arh-rail is-fest" />
            <text x={festX0} y={rail + 20} className="arh-note">
              <tspan x={festX0} className="arh-strong">Festival week</tspan>
              <tspan x={festX0} dy="16">{wide ? festRangeNoYear : festRangeShort}</tspan>
            </text>
          </svg>
        </div>

        <div className="arendal-hero-foot">
          <div className="arendal-meta">
            <span><CalendarDays size={15} aria-hidden="true" />Festival week {festRange}</span>
            <span><MapPin size={15} aria-hidden="true" />Arendal sentrum · {F.area_km2} km²</span>
            <span><Radio size={15} aria-hidden="true" />Privacy-safe network aggregates</span>
          </div>
          <p className="arendal-hero-caption">
            Highest ten-minute reading each day, {windowRange}. Estimated devices: measured on one mobile network and
            scaled to all networks. Outlined columns are part days ({partDays}).
          </p>
        </div>
      </div>
    </header>
  );
};

// ---- daily peaks up close: the last three weeks ----------------------------
const zoomDesc =
  `Daily peak of estimated devices in Arendal sentrum, ${zoomRange}. ` +
  `From ${longDate(zoomPre[0].d)} to ${longDate(zoomPre[zoomPre.length - 1].d)} the daily peak ranged from ${fmt(zoomPreMin)} to ${fmt(zoomPreMax)}. ` +
  `During the festival week it reached ${fmt(openDay.peak)} on ${dayLongDate(openDay.d)} and ${fmt(peakDay.peak)} at ${peakTime} on ${dayLongDate(peakDay.d)}, ` +
  `then fell to ${festWeekend.map((r) => `${fmt(r.peak)} on ${dayLongDate(r.d)}`).join(" and ")}. ` +
  `The median of all July readings, ${fmt(F.baseline)}, is drawn as a dashed line. ` +
  `${dayLongDate(lastDay.d)} is a part day.`;

const DailyPeaksChart = () => {
  const [ref, w] = useWidth(640);
  const [active, setActive] = useState(null);
  const rows = zoomDays;
  const n = rows.length;
  const left = 78;
  const right = 10;
  const pw = w - left - right;
  const step = pw / n;
  // A weekday initial under every column when there is room for one.
  const showDays = step >= 14;
  const m = { top: 52, right, bottom: showDays ? 44 : 30, left };
  const h = (w < 480 ? 280 : 360) + (showDays ? 14 : 0);
  const ph = h - m.top - m.bottom;
  const barW = Math.max(3, Math.min(18, step - 4));
  const yMax = niceMax(peakDay.peak, 1000);
  const x = (i) => m.left + step * i + step / 2;
  const y = (v) => m.top + ph - (v / yMax) * ph;
  const base = y(0);
  const firstFest = rows.findIndex(isFest);
  const lastFest = firstFest + festDays.length - 1;
  const peakAt = rows.indexOf(peakDay);
  const lastIdx = n - 1;
  const bandLeft = m.left + step * firstFest;
  const bandRight = m.left + step * (lastFest + 1);
  const activeRow = active === null ? null : rows[active];
  const mondays = rows.map((r, i) => (r.dow === "Mon" ? i : -1)).filter((i) => i >= 0);
  const dateRow = showDays ? base + 33 : base + 18;
  // The "part day" note sits above the last column; skip it when it would run
  // into a taller column on a narrow chart (the key still names the part days).
  const partLeft = x(lastIdx) + barW / 2 - 46;
  const partLabelFits = rows.every(
    (r, i) => i === lastIdx || x(i) + barW / 2 < partLeft || r.peak < lastDay.peak
  );

  return (
    <div ref={ref} className="arendal-chart-frame">
      <svg
        width="100%"
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-labelledby="daily-peaks-title daily-peaks-desc"
        onMouseLeave={() => setActive(null)}
      >
        <title id="daily-peaks-title">Daily peak of estimated devices in Arendal sentrum, {zoomRange}</title>
        <desc id="daily-peaks-desc">{zoomDesc}</desc>

        <text x="0" y="14" className="ar-axis-title">Daily peak, estimated devices</text>

        <rect x={bandLeft} y={m.top - 8} width={bandRight - bandLeft} height={base - m.top + 8} className="ar-band" />
        <text x={bandRight} y={m.top - 14} textAnchor="end" className="ar-label">Festival week, {festRangeShort}</text>

        {tickValues(yMax, 10000).map((v) => (
          <g key={v}>
            {v > 0 && <line x1={m.left} x2={m.left + pw} y1={y(v)} y2={y(v)} className="ar-grid" />}
            <text x={m.left - 8} y={y(v) + 4} textAnchor="end" className="ar-tick">{fmt(v)}</text>
          </g>
        ))}

        {rows.map((r, i) => (
          <path
            key={r.d}
            d={columnPath(x(i), y(r.peak) + (r.partial ? 0.75 : 0), base, r.partial ? barW - 1.5 : barW)}
            className={r.partial ? "ar-col-part" : isFest(r) ? "ar-col-fest" : "ar-col-normal"}
            opacity={active === null || active === i ? 1 : 0.55}
          />
        ))}

        <line x1={m.left} x2={m.left + pw} y1={base} y2={base} className="ar-baseline" />

        <line x1={m.left} x2={m.left + pw} y1={y(F.baseline)} y2={y(F.baseline)} className="ar-median" />
        <line x1={m.left - 6} x2={m.left} y1={y(F.baseline)} y2={y(F.baseline)} className="ar-median-tick" />
        <text x={m.left - 10} y={y(F.baseline) - 3} textAnchor="end" className="ar-label">
          <tspan x={m.left - 10}>July median</tspan>
          <tspan x={m.left - 10} dy="13">{fmt(F.baseline)}</tspan>
        </text>

        <text x={x(peakAt) - barW / 2 - 6} y={y(peakDay.peak) - 5} textAnchor="end" className="ar-label">
          {fmt(peakDay.peak)} at {peakTime}, {dayShortDate(peakDay.d)}
        </text>

        {lastDay.partial && partLabelFits && (
          <text x={x(lastIdx) + barW / 2} y={y(lastDay.peak) - 8} textAnchor="end" className="ar-note ar-halo">part day</text>
        )}

        {showDays && rows.map((r, i) => (
          <text key={`dow-${r.d}`} x={x(i)} y={base + 15} textAnchor="middle" className="ar-tick ar-dow">
            {r.dow[0]}
          </text>
        ))}
        {mondays.map((i) => {
          // Dates start at the week boundary; the last one ends at the chart edge if it would overflow.
          const at = x(i) - step / 2;
          const fits = at + 42 <= w;
          return (
            <g key={`mon-${i}`}>
              <line x1={at} x2={at} y1={base} y2={dateRow - 11} className="ar-baseline" />
              <text x={fits ? at + 4 : w - 1} y={dateRow} textAnchor={fits ? "start" : "end"} className="ar-tick">
                {shortDate(rows[i].d)}
              </text>
            </g>
          );
        })}

        {rows.map((r, i) => (
          <rect
            key={`hit-${r.d}`}
            x={m.left + step * i}
            y={m.top}
            width={step}
            height={ph}
            className="ar-hit"
            onMouseEnter={() => setActive(i)}
            onClick={() => setActive(i)}
          />
        ))}

        {activeRow && (
          <Tooltip
            x={x(active)}
            y={y(activeRow.peak)}
            bounds={{ left: 2, right: w - 2 }}
            lines={[
              `${dayShortDate(activeRow.d)}, daily peak`,
              `${fmt(activeRow.peak)} estimated devices`,
              ...(activeRow.partial ? ["part day"] : []),
            ]}
          />
        )}
      </svg>
    </div>
  );
};

const FestivalChart = () => (
  <figure className="arendal-chart">
    <DailyPeaksChart />
    <ul className="arendal-legend" aria-label="Chart key">
      <li><i className="arendal-key arendal-key--normal" />Other days</li>
      <li><i className="arendal-key arendal-key--fest" />Festival week, {festRangeShort}</li>
      <li><i className="arendal-key arendal-key--part" />Part day ({zoomPartDays})</li>
      <li><i className="arendal-key arendal-key--median" />Median of all July readings</li>
    </ul>
    <figcaption>
      Highest ten-minute reading each day, Arendal sentrum ({F.area_km2} km²), {zoomRange}: the two weeks before the
      festival week, the week itself and the two days after it. The chart at the top of the page shows the whole period
      from {longDate(firstDay.d)}. Estimated devices: measured on one mobile network and scaled to all networks.
    </figcaption>
    <details className="arendal-table">
      <summary>Show every daily figure, {shortDate(firstDay.d)} to {shortDate(lastDay.d)}</summary>
      <div className="arendal-table-scroll">
        <table>
          <caption className="sr-only">Daily peak of estimated devices, Arendal sentrum, {windowRange}</caption>
          <thead>
            <tr><th scope="col">Date</th><th scope="col">Daily peak</th><th scope="col">Note</th></tr>
          </thead>
          <tbody>
            {arendalDaily.map((r) => (
              <tr key={r.d} className={isFest(r) ? "is-fest" : undefined}>
                <td>{dayShortDate(r.d)}</td>
                <td>{fmt(r.peak)}</td>
                <td>{[isFest(r) && "Festival week", r.partial && "Part day"].filter(Boolean).join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  </figure>
);

// ---- three Tuesdays --------------------------------------------------------
const TuesdayComparison = () => {
  const max = Math.max(...arendalTuesdays.map((t) => t.peak));
  return (
    <figure className="arendal-tuesdays">
      <figcaption>Tuesday peaks a week apart, estimated devices</figcaption>
      <ol>
        {arendalTuesdays.map((t) => {
          const fest = t.date >= F.fest_from && t.date <= F.fest_to;
          return (
            <li key={t.date} className={fest ? "is-fest" : t.partial ? "is-part" : undefined}>
              <div className="arendal-tue-head">
                <span><b>{dayShortDate(t.date)}</b> · {t.label}</span>
                <span className="arendal-tue-value">{fmt(t.peak)} <small>at {t.peakAt}</small></span>
              </div>
              <div className="arendal-tue-track" aria-hidden="true">
                <i style={{ width: `${(t.peak / max) * 100}%` }} />
              </div>
              {t.partial && <small className="arendal-tue-note">Part day: readings end at {t.until}</small>}
            </li>
          );
        })}
      </ol>
      <p>
        The festival Tuesday peak was {times(tueFest.peak, tueBefore.peak)} times the peak a week earlier
        and {times(tueFest.peak, tueAfter.peak)} times the peak a week later.
      </p>
    </figure>
  );
};

// ---- average day by hour ---------------------------------------------------
const hourlyDesc =
  `Average estimated devices by hour of day. Festival week, ${festRange}: highest at ${hh(hourFestPeak.h)} with ${fmt(hourFestPeak.fest)}. ` +
  `Other days from ${windowRange}: highest at ${hh(hourNormPeak.h)} with ${fmt(hourNormPeak.norm)}. ` +
  `Between ${hh(hourAt3.h)} and ${hh(hourAt3.h + 1)} the festival week averaged ${fmt(hourAt3.fest)} against ${fmt(hourAt3.norm)} on other days.`;

const HourlyChart = () => {
  const [ref, w] = useWidth(900);
  const [active, setActive] = useState(null);
  const h = w < 480 ? 260 : 300;
  const m = { top: 30, right: 14, bottom: 32, left: 50 };
  const pw = w - m.left - m.right;
  const ph = h - m.top - m.bottom;
  const last = arendalHourly.length - 1;
  const yMax = niceMax(hourFestPeak.fest, 5000);
  const x = (hour) => m.left + (hour / last) * pw;
  const y = (v) => m.top + ph - (v / yMax) * ph;
  const base = y(0);
  const line = (key) => arendalHourly.map((r, i) => `${i ? "L" : "M"}${x(r.h)},${y(r[key])}`).join("");
  const area = `${line("fest")}L${x(last)},${base}L${x(0)},${base}Z`;
  const hourStep = w < 560 ? 6 : 3;
  const activeRow = active === null ? null : arendalHourly[active];
  const slot = pw / last;

  return (
    <div ref={ref} className="arendal-chart-frame">
      <svg
        width="100%"
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-labelledby="hourly-title hourly-desc"
        onMouseLeave={() => setActive(null)}
      >
        <title id="hourly-title">Average estimated devices by hour of day, festival week against other days</title>
        <desc id="hourly-desc">{hourlyDesc}</desc>

        <text x="0" y="14" className="ar-axis-title">Average estimated devices</text>

        {tickValues(yMax, 5000).map((v) => (
          <g key={v}>
            {v > 0 && <line x1={m.left} x2={m.left + pw} y1={y(v)} y2={y(v)} className="ar-grid" />}
            <text x={m.left - 8} y={y(v) + 4} textAnchor="end" className="ar-tick">{fmt(v)}</text>
          </g>
        ))}
        <line x1={m.left} x2={m.left + pw} y1={base} y2={base} className="ar-baseline" />

        <path d={area} className="ar-area-fest" />
        <path d={line("norm")} className="ar-line-norm" />
        <path d={line("fest")} className="ar-line-fest" />

        <circle cx={x(hourFestPeak.h)} cy={y(hourFestPeak.fest)} r="4.5" className="ar-dot-fest" />
        <text x={x(hourFestPeak.h)} y={y(hourFestPeak.fest) - 26} textAnchor="middle" className="ar-label ar-halo">
          <tspan x={x(hourFestPeak.h)}>Festival week</tspan>
          <tspan x={x(hourFestPeak.h)} dy="14">{fmt(hourFestPeak.fest)} at {hh(hourFestPeak.h)}</tspan>
        </text>
        <circle cx={x(hourNormPeak.h)} cy={y(hourNormPeak.norm)} r="4.5" className="ar-dot-norm" />
        {w >= 480 && (
          <text x={x(hourNormPeak.h)} y={y(hourNormPeak.norm) + 22} textAnchor="middle" className="ar-label ar-halo">
            <tspan x={x(hourNormPeak.h)}>Other days</tspan>
            <tspan x={x(hourNormPeak.h)} dy="14">{fmt(hourNormPeak.norm)} at {hh(hourNormPeak.h)}</tspan>
          </text>
        )}

        {arendalHourly.filter((r) => r.h % hourStep === 0).map((r) => (
          <text key={r.h} x={x(r.h)} y={base + 20} textAnchor="middle" className="ar-tick">{hh(r.h)}</text>
        ))}

        {activeRow && (
          <line x1={x(activeRow.h)} x2={x(activeRow.h)} y1={m.top} y2={base} className="ar-guide" />
        )}

        {arendalHourly.map((r, i) => {
          const x0 = Math.max(m.left, x(r.h) - slot / 2);
          const x1 = Math.min(m.left + pw, x(r.h) + slot / 2);
          return (
            <rect
              key={`hit-${r.h}`}
              x={x0}
              y={m.top}
              width={x1 - x0}
              height={ph}
              className="ar-hit"
              onMouseEnter={() => setActive(i)}
              onClick={() => setActive(i)}
            />
          );
        })}

        {activeRow && (
          <Tooltip
            x={x(activeRow.h)}
            y={y(activeRow.fest)}
            bounds={{ left: 2, right: w - 2 }}
            lines={[
              `${hh(activeRow.h)}–${hh((activeRow.h + 1) % 24)}`,
              `Festival week ${fmt(activeRow.fest)}`,
              `Other days ${fmt(activeRow.norm)}`,
            ]}
          />
        )}
      </svg>
    </div>
  );
};

// ---- page ------------------------------------------------------------------
const stats = [
  {
    label: "Peak against July median",
    value: `${F.lift}×`,
    note: `${fmt(F.peak)} against a median of ${fmt(F.baseline)} across all July readings`,
  },
  {
    label: "Different devices, Mon–Fri",
    value: fmt(F.week_unique),
    note: `estimated, ${ymd(festDays[0].d)[2]}–${longDate(festDays[4].d)}, each device counted once`,
  },
  {
    label: "Foreign SIM cards",
    value: `${F.foreign_fest_pct}%`,
    note: `of devices in the festival week, against ${F.foreign_base_pct}% on other days`,
  },
  {
    label: "Reading interval",
    value: "10 min",
    note: "day and night, 144 readings a day",
  },
];

const ArendalsukaPage = () => (
  <div className="arendal-page">
    <Navbar />
    <main className="pt-16">
      <ArendalHero />

      <section className="arendal-stats" aria-label="Key figures">
        {stats.map(({ label, value, note }) => (
          <div key={label}><small>{label}</small><strong>{value}</strong><span>{note}</span></div>
        ))}
      </section>

      <div className="arendal-wrap">
        <section className="arendal-question">
          <div><p className="arendal-section-label">Background</p><h2>What we measured</h2></div>
          <p>
            Every August, Arendalsuka brings politicians, organisations and members of the public to Arendal for a week of
            debates and meetings. We estimated the number of mobile devices inside a {F.area_km2} km² zone over the town
            centre every ten minutes, day and night, from {windowRange}: five weeks before the festival week of{" "}
            {festRangeNoYear}, the week itself and two days after it.
          </p>
        </section>

        <section className="arendal-signal">
          <div>
            <p className="arendal-section-label">Daily peaks</p>
            <h2>The busiest day was {dayLongDate(peakDay.d)}</h2>
            <p>
              In the five weeks before the festival, the daily peak in the centre ranged from {fmt(preMin)} to{" "}
              {fmt(preMax)} estimated devices. On {dayLongDate(openDay.d)}, the opening day, it reached {fmt(openDay.peak)}.
              The next day it reached {fmt(peakDay.peak)} at {peakTime}. On the weekend of{" "}
              {festWeekend.map((r) => ymd(r.d)[2]).join("–")} {MONTHS_LONG[ymd(festWeekend[0].d)[1] - 1]} it fell to{" "}
              {festWeekend.map((r) => fmt(r.peak)).join(" and ")}, lower than any daily peak in the five weeks before.
            </p>
            <TuesdayComparison />
          </div>
          <FestivalChart />
        </section>
      </div>

      <section className="arendal-actions">
        <div className="arendal-actions-head">
          <p className="arendal-section-label">Hour by hour</p>
          <h2>An average day in the festival week against other days</h2>
          <p>
            Averaged over {festRangeNoYear}, the centre was busiest in the hour from {hh(hourFestPeak.h)}, with{" "}
            {fmt(hourFestPeak.fest)} estimated devices. On other days the busiest hour started at {hh(hourNormPeak.h)},
            with {fmt(hourNormPeak.norm)}. Nights were busier too: between {hh(hourAt3.h)} and {hh(hourAt3.h + 1)} the
            festival week averaged {fmt(hourAt3.fest)}, against {fmt(hourAt3.norm)} on other days.
          </p>
        </div>
        <figure className="arendal-hourly arendal-chart">
          <HourlyChart />
          <ul className="arendal-legend" aria-label="Chart key">
            <li><i className="arendal-key arendal-key--line-fest" />Festival week, {festRangeShort}</li>
            <li><i className="arendal-key arendal-key--line-norm" />Other days, {shortDate(firstDay.d)}–{shortDate(lastDay.d)}</li>
          </ul>
          <figcaption>
            Mean of the ten-minute readings in each hour of the day, Arendal sentrum. The festival-week average includes{" "}
            {festWeekend.map((r) => dayLongDate(r.d)).join(" and ")}, which had lower daily peaks than any full day in the
            five weeks before.
          </figcaption>
        </figure>
      </section>

      <section className="arendal-parking arendal-wrap">
        <div>
          <p className="arendal-section-label">Parking</p>
          <h2>Most parking sits away from the venues</h2>
          <p>
            OpenStreetMap lists 153 parking areas in and around Arendal, with about 10,500 spaces between them. 724 of
            those spaces, about 7%, are within 600 m of the festival venues. These are capacities from map data. Our
            device counts cover only the town-centre zone, so they do not show how full each car park was.
          </p>
        </div>
        <div
          className="arendal-parking-figure"
          role="img"
          aria-label="7% of parking spaces in Arendal are within 600 metres of the festival venues: 724 of about 10,500. Each of 100 squares is 1%, and 7 are highlighted."
        >
          <strong>7%</strong><span>of spaces within 600 m of the venues</span>
          <div aria-hidden="true">
            {Array.from({ length: 100 }).map((_, i) => <i key={i} className={i < 7 ? undefined : "muted"} />)}
          </div>
          <small>Each square is 1% of the town's parking spaces.</small>
        </div>
      </section>

      <section className="arendal-method" aria-labelledby="arendal-method-title">
        <div>
          <h2 id="arendal-method-title">About the figures</h2>
          <p className="arendal-method-unit">
            Unit: estimated devices · aggregated and privacy-safe · one network scaled to estimate all networks
          </p>
          <ul>
            <li>
              Device counts cover a {F.area_km2} km² zone over Arendal sentrum, one reading every ten minutes. They are
              measured on one mobile network and scaled up by that network's estimated market share to cover all
              networks.
            </li>
            <li>
              The figures count mobile devices: one person carrying two phones counts twice, and residents and people
              passing through are included.
            </li>
            <li>
              The peak is the highest single reading. The July median ({fmt(F.baseline)}) is the median of every ten-minute
              reading from {ymd(firstDay.d)[2]} to 31 July, day and night, so the {F.lift}× ratio compares a peak moment
              with a typical moment.
            </li>
            <li>
              The different-devices figure ({fmt(F.week_unique)}) counts each device once from Monday to Friday of the
              festival week, and is scaled the same way. The foreign SIM share is the share of devices with a SIM card from outside
              Norway. It is not scaled.
            </li>
            <li>
              {dayLongDate(firstDay.d)} and {dayLongDate(lastDay.d)} are part days. The last reading is at{" "}
              {lastReading} on {longDate(lastDay.d)}.
            </li>
            <li>Parking areas and capacities: © OpenStreetMap contributors, ODbL.</li>
          </ul>
        </div>
      </section>

      <CaseStudyCta
        question="Hosting an event in a town centre and want to know how full it gets, and when?"
        body="We can estimate devices in your event zone every ten minutes and compare the event with ordinary weeks before and after it."
        extra={
          <a
            href="https://arendalsuka.web.app"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-semibold text-ink underline underline-offset-4 hover:text-brand-700"
          >
            See the full Arendalsuka analysis
            <ExternalLink size={14} aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        }
      />
    </main>
  </div>
);

export default ArendalsukaPage;
